import type { FormApi } from '../../form-hooks/useFormApi';
import type { ContentTypeFormSubmitFn } from '../../form/ContentTypeForm/ContentTypeForm';
import type {
  Media,
  MediaHydrated,
} from '../../lib/flotiq-client/types/api-response';

import { useQueryClient } from '@tanstack/react-query';
import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { postContentType } from '../../lib/flotiq-client';
import {
  getCtdFeaturedImage,
  markTaskAsDone,
} from '../../lib/flotiq-client/api-helpers';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';
import {
  invalidateContentObjects,
  invalidateContentTypes,
} from '../../lib/queryClientHelpers';

import Button from '../../components/Button/Button';
import ContentObjectInformation from '../../components/ContentObjectInformation/ContentObjectInformation';
import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import FileButton from '../../components/FileButton/FileButton';
import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';
import LinkObjectContentModal from '../../components/LinkObjectContentModal/LinkObjectContentModal';
import Loader from '../../components/Loader/Loader';
import PlainObjectEditor from '../../components/PlainObjectEditor/PlainObjectEditor';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarDuplicateButton from '../../components/Topbar/buttons/TopbarDuplicateButton';
import TopbarSynchronizationButton from '../../components/Topbar/buttons/TopbarExportButton';
import TopbarSaveAndLeaveButton from '../../components/Topbar/buttons/TopbarSaveAndLeaveButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';

import { useContentObject, useContentType } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useMediaUpload from '../../hooks/api/useMediaUpload';
import { useSpaceConstraint } from '../../hooks/api/useSpaceConstraint';
import useSpaceSynchronization from '../../hooks/synchronization/useSpaceSynchronization';
import useFirstLoading from '../../hooks/useFirstLoading';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import DirtyHandlerContext from '../../contexts/DirtyHandlerContext';
import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { DeleteIcon, ExportIcon, WarningIcon } from '../../images/shapes';

import ContentPreviewForm from '../../form/ContentPreviewForm/ContentPreviewForm';
import ContentTypeForm from '../../form/ContentTypeForm/ContentTypeForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import { DEFINED_TYPES } from '../ContentTypeDefinitions/definedTypes';
import FormGeneratorPanel from './formGenerator/FormGeneratorPanel';

const getFeaturedImage = (featuredImage?: Media) =>
  featuredImage?.id
    ? [
        {
          dataUrl: `/api/v1/content/_media/${featuredImage.id}`,
          type: 'internal',
        },
      ]
    : [];

const testId = 'add-content-type-definition';

interface AddContentTypeDefinitionProps {
  /**
   * If type definition is duplicating
   */
  duplicate?: boolean;
}

const AddContentTypeDefinition = ({
  duplicate = false,
}: AddContentTypeDefinitionProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const modal = useModals();
  const jwt = useToken();
  const navigate = useNavigate();
  const navigateOnSave = useRef<boolean>(false);
  const { onUpload } = useMediaUpload();
  const { setDirty } = use(DirtyHandlerContext);
  const [user, setUser] = useLocalStorageState('cms.user');

  const [formState, setFormState] = useState<FormApi>();

  const { space, buildUrlWithSpace } = useSelectedSpace();
  const { handleExport } = useSpaceSynchronization(space);
  const { permissions } = use(UserContext);

  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get('defaultType');

  const { contentTypeName } = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { canCreate, canDelete, canUpdate, canRead } = useMemo(
    () => permissions.getCtdPermissions(contentTypeName) || {},
    [contentTypeName, permissions],
  );

  const { gridLink } = useGridNavigate(
    `objects-${contentTypeName}`,
    buildUrlWithSpace(`content-type-objects/${contentTypeName}`),
  );

  const { reload: reloadConstraint } = useSpaceConstraint(
    'content-definitions-count',
  );

  const {
    entity: contentType,
    isLoading: contentTypeIsLoading,
    updateEntity: updateContentType,
    deleteEntity: deleteContentType,
    errors: contentTypeErrors,
    status: contentTypeStatus,
  } = useContentType(contentTypeName);

  const firstLoading = useFirstLoading(
    !contentTypeIsLoading || !contentTypeName,
    contentTypeName,
  );

  const definedType = useMemo(() => {
    if (!defaultType) return;
    return defaultType in DEFINED_TYPES
      ? DEFINED_TYPES[defaultType as keyof typeof DEFINED_TYPES]?.ctd
      : undefined;
  }, [defaultType]);

  const [currentFeaturedImage, setCurrentFeaturedImage] = useState<Media>();

  const { entity: featuredImage, errors: featuredImageErrors } =
    useContentObject(
      '_media',
      contentType?.featuredImage?.[0]?.dataUrl?.match(/[^/]+$/)?.[0],
    );

  useEffect(() => {
    if (featuredImage) setCurrentFeaturedImage(featuredImage as Media);
  }, [featuredImage]);

  useApiErrorsToast(contentTypeErrors);
  useApiErrorsToast(featuredImageErrors);

  const handleDeleteType = useCallback(async () => {
    modal.deleting('delete-modal');
    try {
      const { body, status, ok } = await deleteContentType({
        contentTypeName,
      });
      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      toast.success(t('ContentDefinition.Deleted', { type: contentTypeName }));

      setDirty(false);
      navigate(buildUrlWithSpace('content-type-definitions'));
      invalidateContentTypes(queryClient, contentTypeName);
      reloadConstraint();
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
      } else {
        toast.error(
          error.message
            ? error.message
            : t('ContentDefinition.DeletingError', { type: contentTypeName }),
        );
        setIsSaving(false);
      }
    }
  }, [
    modal,
    deleteContentType,
    contentTypeName,
    t,
    setDirty,
    navigate,
    buildUrlWithSpace,
    queryClient,
    reloadConstraint,
  ]);

  const deleteType = useCallback(async () => {
    setIsDeleting(true);
    await modal.delete(
      t('ContentDefinition.ConfirmDelete'),
      'delete-modal',
      () => handleDeleteType(),
    );
    setIsDeleting(false);
  }, [handleDeleteType, modal, t]);

  const pageTitle = useMemo(() => {
    if (duplicate) return 'Duplicate';
    else if (contentTypeName) return 'Edit';
    return 'Add';
  }, [duplicate, contentTypeName]);

  const notAllowed = useMemo(
    () => contentType?.internal || (contentTypeName && !canRead),
    [contentType?.internal, contentTypeName, canRead],
  );

  const label = useMemo(
    () => contentType?.label || contentTypeName,
    [contentType?.label, contentTypeName],
  );

  const validateFeaturedImage = useCallback(
    (value?: Media | MediaHydrated) => {
      const mediaData = Array.isArray(value) ? value[0] : value;
      const isValid = !mediaData || mediaData.type === 'image';

      if (!isValid) toast.error(t('ContentTypeForm.FeaturedImage.Invalid'));
      return isValid;
    },
    [t],
  );

  const saveNewType = useCallback<ContentTypeFormSubmitFn>(
    async (values) => {
      setIsSaving(true);

      if (!validateFeaturedImage(currentFeaturedImage)) {
        setIsSaving(false);
        return [values, {}, true];
      }

      try {
        const { body, status, ok } = await postContentType(jwt, space, {
          ...values,
          featuredImage: getFeaturedImage(currentFeaturedImage),
        });
        setIsSaving(false);
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        toast.success(
          t('ContentTypeForm.Added', { contentTypeName: body.label }),
        );
        setDirty(false);

        TagManager.dataLayer({
          dataLayer: {
            event: 'ctd_change',
          },
        });

        navigate(
          buildUrlWithSpace(`content-type-definitions/edit/${body.name}`),
        );

        markTaskAsDone('contentType', user, jwt, setUser, body.name);

        invalidateContentTypes(queryClient, contentTypeName);
        reloadConstraint();
        return [body, {}, false];
      } catch (error) {
        setIsSaving(false);
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [values, {}, true];
        }

        if (!error.errors || !Object.keys(error.errors).length) {
          toast.error(t('ContentTypeForm.AddingError'));
          return [values, {}, true];
        }

        return [
          values,
          typeof error.errors === 'string'
            ? { '': error.errors }
            : error.errors,
          true,
        ];
      }
    },
    [
      validateFeaturedImage,
      currentFeaturedImage,
      jwt,
      space,
      t,
      setDirty,
      navigate,
      buildUrlWithSpace,
      user,
      setUser,
      queryClient,
      contentTypeName,
      reloadConstraint,
    ],
  );

  const updateType = useCallback<ContentTypeFormSubmitFn>(
    async (values) => {
      setIsSaving(true);

      if (!validateFeaturedImage(currentFeaturedImage)) {
        setIsSaving(false);
        return [values, {}, true];
      }

      try {
        const { status, body, ok } = await updateContentType({
          ...values,
          featuredImage: getFeaturedImage(currentFeaturedImage),
          contentTypeName,
        });
        setIsSaving(false);
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        toast.success(
          t('ContentTypeForm.Saved', { contentTypeName: body.label }),
        );
        setDirty(false);

        TagManager.dataLayer({
          dataLayer: {
            event: 'ctd_change',
          },
        });

        invalidateContentObjects(queryClient, contentTypeName);
        invalidateContentTypes(queryClient, contentTypeName);
        reloadConstraint();

        return [body, {}, false];
      } catch (error) {
        setIsSaving(false);

        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [values, {}, true];
        }

        if (!error.errors || !Object.keys(error.errors).length) {
          toast.error(t('ContentTypeForm.SavingError', { contentTypeName }));
          return [values, {}, true];
        }

        return [
          values,
          typeof error.errors === 'string'
            ? { '': error.errors }
            : error.errors,
          true,
        ];
      }
    },
    [
      validateFeaturedImage,
      currentFeaturedImage,
      updateContentType,
      contentTypeName,
      t,
      setDirty,
      queryClient,
      reloadConstraint,
    ],
  );

  const openMediaModal = useCallback(async () => {
    const relation = await modal({
      title: (
        <div className="flex flex-wrap justify-between gap-1">
          <div className="text-3xl">{t('Global.MediaLibrary')}</div>
          <FileButton
            onUpload={onUpload}
            multiple
            additionalClasses="w-fit"
            testId={testId}
          />
        </div>
      ),
      content: (
        <LinkObjectContentModal
          relationType="_media"
          onMediaUpload={onUpload}
          returnContentObject
          validateSelected={(value) =>
            validateFeaturedImage(value ? Object.values(value)[0] : undefined)
          }
          defaultUserMediaFilters={{
            type: { filter: 'image', type: 'equals' },
          }}
          {...getTestProps(testId, 'link-media', 'testId')}
        />
      ),
      size: '2xl',
      dialogAdditionalClasses: 'h-[calc(100vh-32px)]',
    });

    const mediaData = relation ? Object.values(relation)[0] : [];
    if (mediaData) {
      setCurrentFeaturedImage(mediaData as Media);
      setDirty(true);
    }
  }, [modal, t, onUpload, validateFeaturedImage, setDirty]);

  const showForm = useMemo(
    () => !notAllowed && !firstLoading && (contentType || !contentTypeName),
    [notAllowed, firstLoading, contentType, contentTypeName],
  );

  const emptyCTD = useMemo(() => {
    if (showForm) return null;
    if (firstLoading) {
      return (
        <Loader
          size="small"
          type="spinner-grid"
          {...getTestProps(testId, 'loader', 'testId')}
        />
      );
    }
    if (notAllowed) {
      toast.error(
        t('ContentDefinition.CouldntFind', { contentTypeName: label }),
      );
    }
    return (
      <Heading
        level={2}
        additionalClasses={twMerge(
          'text-3xl md:text-4xl leading-8 dark:text-slate-200',
        )}
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon
            className="text-red w-14 md:w-20 mb-3"
            title={t('ContentDefinition.CouldntFind')}
          />
          {contentTypeStatus === 404 || notAllowed
            ? t('ContentDefinition.CouldntFind', { contentTypeName })
            : t('ContentForm.CouldntFetch')}
        </div>
      </Heading>
    );
  }, [
    showForm,
    firstLoading,
    notAllowed,
    t,
    contentTypeStatus,
    contentTypeName,
    label,
  ]);

  const finalContentType = useMemo(() => {
    if (defaultType) {
      return definedType;
    }
    if (duplicate && contentType) {
      return {
        label: contentType.label,
        name: contentType.name,
        autoSave: contentType.autoSave,
        draftPublic: contentType.draftPublic,
        metaDefinition: contentType.metaDefinition,
        schemaDefinition: contentType.schemaDefinition,
        slugs: contentType.slugs,
      };
    }
    return contentType;
  }, [contentType, definedType, defaultType, duplicate]);

  const isFormDisabled = useMemo(() => {
    if (isSaving || isDeleting) return true;
    if (contentTypeName) return !canUpdate;
    return !canCreate;
  }, [canCreate, canUpdate, contentTypeName, isDeleting, isSaving]);

  const deleteFeaturedImage = useCallback(() => {
    setCurrentFeaturedImage(undefined);
    setDirty(true);
  }, [setDirty]);

  const featuredImageUrl = getCtdFeaturedImage(
    contentType?.name,
    currentFeaturedImage,
    1000,
  );

  const isSaveAllowed =
    (canUpdate && contentTypeName) || (canCreate && !contentTypeName);

  return (
    <PageLayout
      page="definitionBuilder"
      title={t(`ContentTypeForm.${pageTitle}`, { contentTypeName: label })}
      buttonsDisabled={isSaving || isDeleting}
      breadcrumbs={
        <TopbarBreadcrumbs
          parentTitle={t('Global.DefinitionBuilder')}
          parentLink={buildUrlWithSpace('content-type-definitions')}
        />
      }
      buttons={
        <>
          <TopbarCancelButton
            isDirty={formState?.dirty}
            link={buildUrlWithSpace('content-type-definitions')}
          />
          <TopbarSaveButton
            form="ctd-form"
            isLoading={isSaving}
            navigateOnSave={navigateOnSave}
            actionDisallowed={!isSaveAllowed}
          />
          <TopbarActionMenu>
            {contentTypeName && !duplicate && (
              <TopbarDuplicateButton
                link={buildUrlWithSpace(
                  `content-type-definitions/duplicate/${contentTypeName}`,
                )}
                actionDisallowed={!canCreate}
              />
            )}
            {contentTypeName && (
              <TopbarSynchronizationButton
                onClick={() =>
                  handleExport('exportCTD', finalContentType?.name || '')
                }
                iconImage={<ExportIcon className="w-5 mb-px" />}
                label={t('SpaceSynchronization.Export.ExportButton')}
                testIdSuffix="export"
              />
            )}
            <TopbarSaveAndLeaveButton
              form="ctd-form"
              navigateOnSave={navigateOnSave}
              actionDisallowed={!isSaveAllowed}
            />
            {contentTypeName && !duplicate && (
              <TopbarDeleteButton
                onClick={deleteType}
                actionDisallowed={!canDelete}
              />
            )}
          </TopbarActionMenu>
        </>
      }
      testId={testId}
    >
      {showForm ? (
        <div className={predefinedLayoutClasses.withSidebar}>
          <div className={predefinedLayoutClasses.leftColumnWhite}>
            <ContentTypeForm
              key={pageTitle}
              contentType={finalContentType}
              isEditing={!!(contentTypeName && !duplicate)}
              onSubmit={
                contentTypeName && !duplicate ? updateType : saveNewType
              }
              disabled={isFormDisabled}
              navigateOnSave={navigateOnSave}
              onMediaUpload={onUpload}
              setFormState={setFormState}
              testId={testId}
            />
          </div>
          <div className={predefinedLayoutClasses.rightColumn}>
            <div className="mx-auto relative">
              <img
                key={featuredImageUrl}
                className="w-full rounded-lg object-cover aspect-[1.5]"
                src={featuredImageUrl}
                alt={`${contentType?.label || 'new-content-type'}`}
              />
              <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex gap-2">
                <Button
                  buttonColor="blueBordered"
                  onClick={openMediaModal}
                  additionalClasses={
                    'bg-white/70 dark:bg-white/70 dark:text-slate-900 dark:disabled:hover:bg-white/70'
                  }
                  disabled={isFormDisabled}
                  {...getTestProps(testId, 'change-featured-image', 'testId')}
                >
                  {t('ContentTypeForm.FeaturedImage.Change')}
                </Button>
                <Button
                  buttonColor="redBordered"
                  onClick={deleteFeaturedImage}
                  iconImage={<DeleteIcon className="w-3" />}
                  additionalClasses={
                    'bg-white/70  dark:bg-white/70 dark:text-slate-900 dark:disabled:hover:bg-white/70'
                  }
                  disabled={isFormDisabled}
                  {...getTestProps(testId, 'delete-featured-image', 'testId')}
                />
              </div>
            </div>
            <ContentObjectInformation
              createdAt={duplicate ? null : contentType?.createdAt}
              updatedAt={duplicate ? null : contentType?.updatedAt}
              testId={testId}
            >
              {!duplicate &&
                contentType &&
                permissions.canCo(contentTypeName) && (
                  <LinkButton
                    link={gridLink}
                    additionalClasses="mb-2"
                    {...getTestProps(testId, 'goto-objects-list-btn')}
                  >
                    {t('Global.GoToObjects')}
                  </LinkButton>
                )}
            </ContentObjectInformation>

            {!duplicate && contentTypeName && <ContentPreviewForm />}

            {!duplicate && contentType && (
              <FormGeneratorPanel contentType={contentType} testId={testId} />
            )}

            <PlainObjectEditor
              onSubmit={
                contentTypeName && !duplicate ? updateType : saveNewType
              }
              defaultValue={
                contentType && !duplicate
                  ? JSON.stringify(contentType, null, 2)
                  : ''
              }
              formState={formState}
              {...getTestProps(testId, 'plain-object-editor', 'testId')}
            />
          </div>
        </div>
      ) : (
        <div className={predefinedLayoutClasses.whiteBox}>{emptyCTD}</div>
      )}
    </PageLayout>
  );
};

export default AddContentTypeDefinition;
